import {useEffect, useState} from 'react';
import {Layout} from '@/components/Layout';
import {
    Plus, Search, Mail, Phone, Car, Shield, Wrench, Eye,
    Edit, Trash2, CheckCircle2, XCircle, User,
    Send, ShieldCheck, RotateCw,
} from 'lucide-react';
import {Button} from '@/components/ui/button';
import {Input} from '@/components/ui/input';
import {Card} from '@/components/ui/card';
import {Badge} from '@/components/ui/badge';
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from '@/components/ui/select';
import {Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter} from '@/components/ui/dialog';
import {Label} from '@/components/ui/label';
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from '@/components/ui/table';
import {toast} from 'sonner';
import {fetchUsers, fetchVehicles, mockVehicles, mockChargers, mockOtherObjects, TYPE_LABELS} from '@/data/api';
import {ChevronDown, Check} from 'lucide-react';
import {Popover, PopoverContent, PopoverTrigger} from '@/components/ui/popover';
import {useAuth} from '@/contexts/AuthContext';
import {usePermissions} from '@/hooks/usePermissions';
import {api, getApiErrorMessage, permissionEndpoints, roleEndpoints, userEndpoints, userResourceAssignmentEndpoints} from '@/lib/api';
import {PERMISSIONS} from '@/lib/permissions';

const collectionPayload = (payload, key) => {
    const source = payload?.data ?? payload;
    if (Array.isArray(source)) return source;
    if (Array.isArray(source?.data)) return source.data;
    if (Array.isArray(source?.[key])) return source[key];
    return [];
};

const toLocalPhone = (value = '') => {
    const digits = String(value || '').replace(/\D/g, '');
    const withoutCountryCode = digits.length > 9 && digits.startsWith('351') ? digits.slice(3) : digits;
    return withoutCountryCode.slice(0, 9);
};

const arrayFrom = (value) => {
    if (Array.isArray(value)) return value;
    if (value && typeof value === 'object') return Object.values(value);
    return [];
};

const resourceFromAssignment = (assignment = {}) =>
    assignment.resource
    ?? assignment.gest_resource
    ?? assignment.company_resource
    ?? assignment.resourceable
    ?? assignment;

const resourceReference = (resource = {}) =>
    resource.reference ?? resource.code ?? resource.name ?? resource.title ?? null;

const resourceId = (resource = {}) =>
    resource.id ?? resource.resource_id ?? resource.gest_resource_id ?? resource.company_resource_id ?? null;

const uniqueValues = (values = []) =>
    [...new Set(values.map(value => value == null ? '' : String(value)).filter(Boolean))];

const toApiId = (value) => {
    if (value === undefined || value === null || value === '') return null;
    const numeric = Number(value);
    return Number.isNaN(numeric) ? value : numeric;
};

const userResourceRelations = (source = {}) => [
    ...arrayFrom(source.user_resource_assignments),
    ...arrayFrom(source.resource_user_assignments),
    ...arrayFrom(source.assignments),
    ...arrayFrom(source.resources),
    ...arrayFrom(source.gest_resources),
    ...arrayFrom(source.company_resources),
];

const resourceIdFromAssignment = (assignment = {}) => {
    const resource = resourceFromAssignment(assignment);
    return assignment.resource_id
        ?? assignment.gest_resource_id
        ?? assignment.company_resource_id
        ?? assignment.resourceId
        ?? resource.id
        ?? resource.resource_id
        ?? null;
};

const resourceIdsFromAssignmentsPayload = (payload = {}) => {
    const assignments = collectionPayload(payload, 'user_resource_assignments');
    const items = assignments.length ? assignments : collectionPayload(payload, 'assignments');
    return uniqueValues(items.map(resourceIdFromAssignment));
};

const userResourceReferences = (source = {}) => {
    const directReferences = arrayFrom(
        source.resource_references
        ?? source.resourceReferences
        ?? source.resource_reference_list
        ?? []
    );
    const relationReferences = userResourceRelations(source)
        .map(relation => resourceReference(resourceFromAssignment(relation)));

    return uniqueValues(directReferences.length ? directReferences : relationReferences);
};

const userResourceIds = (source = {}, fallback = {}) => {
    const directIds = arrayFrom(
        source.resource_ids
        ?? source.resourceIds
        ?? source.gest_resource_ids
        ?? source.company_resource_ids
        ?? []
    );
    const relationIds = userResourceRelations(source)
        .map(relation => resourceId(resourceFromAssignment(relation)));
    const fallbackIds = arrayFrom(fallback.resourceIds);

    return uniqueValues(directIds.length ? directIds : relationIds.length ? relationIds : fallbackIds);
};

const userResourceAssignmentPayload = (userId, resourceId) => ({
    user_id: toApiId(userId),
    resource_id: toApiId(resourceId),
});

const permissionKey = (permission) =>
    String(permission?.name || permission?.slug || permission?.key || permission?.id || '').trim();

const permissionLabel = (permission, key) =>
    permission?.label || permission?.display_name || permission?.displayName || permission?.title || humanizeRoleName(key);

const normalizePermissions = (payload) =>
    collectionPayload(payload, 'permissions').reduce((acc, permission) => {
        const key = permissionKey(permission);
        if (!key) return acc;
        acc.push({
            id: key,
            label: permissionLabel(permission, key),
            desc: permission?.description || permission?.desc || '',
            raw: permission,
        });
        return acc;
    }, []);

const rolePermissionKeys = (role) => {
    const permissions = role?.permissions || role?.default_permissions || role?.defaultPermissions || [];
    if (!Array.isArray(permissions)) return [];
    return permissions.map(permission => (
        typeof permission === 'string' ? permission : permissionKey(permission)
    )).filter(Boolean);
};

const defaultPermissionsByRole = (payload) =>
    rolesPayload(payload).reduce((acc, role) => {
        const key = roleKey(role);
        if (!key) return acc;
        acc[key] = rolePermissionKeys(role);
        return acc;
    }, {});

const defaultPermissionsMap = (payload) => {
    const source = payload?.data ?? payload;
    const map = source?.default_permissions_by_role || source?.defaultPermissionsByRole || source?.role_permissions || source?.rolePermissions;
    if (!map || Array.isArray(map) || typeof map !== 'object') return {};

    return Object.entries(map).reduce((acc, [role, permissions]) => {
        acc[role] = Array.isArray(permissions)
            ? permissions.map(permission => (typeof permission === 'string' ? permission : permissionKey(permission))).filter(Boolean)
            : [];
        return acc;
    }, {});
};

const effectivePermissions = (user, defaultsByRole) => {
    if (user.permissions && user.permissions.length >= 0 && user._customPermissions) return user.permissions;
    const set = new Set();
    (user.roles || []).forEach(r => (defaultsByRole[r] || []).forEach(p => set.add(p)));
    return Array.from(set);
};

const userPayload = ({fn, ln, nickname, email, phone, roles, vinfo, customPerms, permissions}) => ({
    first_name: fn,
    last_name: ln,
    name: `${fn} ${ln}`.trim(),
    nickname: nickname || null,
    email,
    phone: phone || null,
    roles,
    vehicle: vinfo.vehicle,
    resource_ids: vinfo.resourceIds,
    resourceIds: vinfo.resourceIds,
    gest_resource_ids: vinfo.resourceIds,
    company_resource_ids: vinfo.resourceIds,
    resource_references: vinfo.resourceReferences,
    status: 'active',
    custom_permissions: customPerms,
    permissions: customPerms ? permissions : [],
});

const normalizeUserResponse = (payload, fallback) => {
    const source = payload?.data?.user ?? payload?.user ?? payload?.data ?? payload;
    if (!source || Array.isArray(source) || typeof source !== 'object') return fallback;

    const firstName = source.firstName ?? source.first_name ?? fallback.firstName;
    const lastName = source.lastName ?? source.last_name ?? fallback.lastName;
    const roles = Array.isArray(source.roles)
        ? source.roles.map(role => (typeof role === 'string' ? role : roleKey(role))).filter(Boolean)
        : fallback.roles;
    const permissions = Array.isArray(source.permissions)
        ? source.permissions.map(permission => (typeof permission === 'string' ? permission : permissionKey(permission))).filter(Boolean)
        : fallback.permissions;
    const resourceReferences = userResourceReferences(source);
    const normalizedResourceReferences = resourceReferences.length
            ? resourceReferences
        : arrayFrom(fallback.resourceReferences);
    const firstResourceReference = normalizedResourceReferences[0] ?? null;
    const createdAt = source.createdAt
        ?? (source.created_at ? String(source.created_at).split('T')[0] : undefined)
        ?? fallback.createdAt;

    return {
        ...fallback,
        ...source,
        id: source.id ?? source.user_id ?? fallback.id,
        firstName,
        lastName,
        name: source.name ?? `${firstName} ${lastName}`.trim(),
        nickname: source.nickname ?? source.nick_name ?? fallback.nickname,
        email: source.email ?? fallback.email,
        phone: toLocalPhone(source.phone ?? source.phone_number ?? fallback.phone),
        roles,
        vehicle: source.resource_reference ?? source.vehicle_reference ?? source.vehicle ?? source.vehicle_name ?? fallback.vehicle,
        resourceReference: source.resource_reference ?? source.vehicle_reference ?? firstResourceReference ?? fallback.resourceReference,
        resourceReferences: normalizedResourceReferences,
        resourceIds: userResourceIds(source, fallback),
        status: source.status ?? fallback.status,
        createdAt,
        _customPermissions: source.custom_permissions ?? source.customPermissions ?? fallback._customPermissions,
        permissions,
    };
};

const userFallback = (source = {}, index = 0) => {
    const firstName = source.firstName ?? source.first_name ?? '';
    const lastName = source.lastName ?? source.last_name ?? '';
    const derivedName = `${firstName} ${lastName}`.trim();
    const name = source.name || derivedName || source.email || `Utilizador ${index + 1}`;

    return {
        id: source.id ?? source.user_id ?? source.email ?? index + 1,
        firstName,
        lastName,
        name,
        nickname: source.nickname ?? source.nick_name ?? '',
        email: source.email ?? '',
        phone: toLocalPhone(source.phone ?? source.phone_number ?? ''),
        roles: [],
        vehicle: null,
        resourceReference: null,
        resourceReferences: [],
        resourceIds: [],
        status: source.status ?? 'active',
        createdAt: source.createdAt ?? (source.created_at ? String(source.created_at).split('T')[0] : ''),
        _customPermissions: source.custom_permissions ?? source.customPermissions ?? false,
        permissions: undefined,
    };
};

const normalizeUsers = (payload) =>
    collectionPayload(payload, 'users').map((user, index) => normalizeUserResponse(user, userFallback(user, index)));

const randomPassword = (len = 10) => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
    return Array.from({length: len}, () => chars[Math.floor(Math.random() * chars.length)]).join('');
};

const ROLE_PRESENTATION = {
    condutor: {icon: Car, color: 'bg-blue-100 text-blue-700'},
    gestor: {icon: Shield, color: 'bg-purple-100 text-purple-700'},
    manutencao: {icon: Wrench, color: 'bg-amber-100 text-amber-700'},
    vigilante: {icon: Eye, color: 'bg-emerald-100 text-emerald-700'},
    default: {icon: User, color: 'bg-slate-100 text-slate-700'},
};

const humanizeRoleName = (value = '') =>
    value
        .replace(/[_-]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/\b\w/g, char => char.toUpperCase());

const rolesPayload = (payload) => {
    const source = payload?.data ?? payload;
    if (Array.isArray(source)) return source;
    if (Array.isArray(source?.data)) return source.data;
    if (Array.isArray(source?.roles)) return source.roles;
    return [];
};

const roleKey = (role) =>
    String(role?.name || role?.slug || role?.key || role?.id || '').trim();

const roleLabel = (role, key) =>
    role?.label || role?.display_name || role?.displayName || role?.title || humanizeRoleName(key);

const normalizeRoles = (payload) =>
    rolesPayload(payload).reduce((acc, role) => {
        const key = roleKey(role);
        if (!key) return acc;
        const presentation = ROLE_PRESENTATION[key] || ROLE_PRESENTATION.default;
        acc[key] = {
            ...presentation,
            id: role?.id,
            label: roleLabel(role, key),
            desc: role?.description || role?.desc || '',
            raw: role,
        };
        return acc;
    }, {});

// Lista enriquecida de objetos da frota para o multi-picker — derivada do mock central
const buildEnrichedObjects = (vehicles = mockVehicles, chargers = mockChargers, others = mockOtherObjects) => {
    const items = [];
    vehicles.forEach(v => {
        const identifier = v.reference;
        items.push({
            uid: identifier, id: v.id, uuid: v.uuid, identifier, type: 'veiculo',
            name: v.name,
            category: v.category, tagId: v.tag?.id || null, hasDriver: !!v.driver,
            driverName: v.driver?.name || null,
        });
    });
    chargers.forEach(c => {
        const identifier = c.reference || c.code;
        items.push({
            uid: identifier, id: c.id, uuid: c.uuid, identifier, type: 'carregador',
            name: c.name,
            category: c.category, tagId: c.tag?.id || null, hasDriver: false, driverName: null,
        });
    });
    others.forEach(o => {
        const identifier = o.reference || o.code;
        items.push({
            uid: identifier, id: o.id, uuid: o.uuid, identifier, type: 'outro',
            name: o.name,
            category: o.category, tagId: o.tag?.id || null, hasDriver: false, driverName: null,
        });
    });
    return items;
};

export const UsersManagement = () => {
    const [users, setUsers] = useState([]);
    const [usersLoading, setUsersLoading] = useState(true);
    const [usersError, setUsersError] = useState('');
    const [userRoles, setUserRoles] = useState({});
    const [permissionOptions, setPermissionOptions] = useState([]);
    const [defaultRolePermissions, setDefaultRolePermissions] = useState({});
    const [rolesLoading, setRolesLoading] = useState(true);
    const [rolesError, setRolesError] = useState('');
    const [permissionsLoading, setPermissionsLoading] = useState(true);
    const [permissionsError, setPermissionsError] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [roleFilter, setRoleFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [isCreateOpen, setIsCreateOpen] = useState(false);
    const [isEditOpen, setIsEditOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [createSubmitting, setCreateSubmitting] = useState(false);
    const [editSubmitting, setEditSubmitting] = useState(false);
    const [userActionSubmitting, setUserActionSubmitting] = useState('');
    const [confirmationAction, setConfirmationAction] = useState(null);

    // Form states
    const [formFirstName, setFormFirstName] = useState('');
    const [formLastName, setFormLastName] = useState('');
    const [formNickname, setFormNickname] = useState('');
    const [formEmail, setFormEmail] = useState('');
    const [formPhone, setFormPhone] = useState('');
    const [formRoles, setFormRoles] = useState([]);
    const [formResourceReferences, setFormResourceReferences] = useState([]); // multi-select de objetos
    const [pickerOpen, setPickerOpen] = useState(false);
    const [pickerSearch, setPickerSearch] = useState('');
    const [enrichedObjects, setEnrichedObjects] = useState(() => buildEnrichedObjects());
    const [formPermissions, setFormPermissions] = useState([]);
    const [formCustomPerms, setFormCustomPerms] = useState(true);

    // Convite por email (pedido d)
    const {adminResetPassword} = useAuth();
    const {permissions: currentPermissions} = usePermissions();
    const canReadUsers = currentPermissions.includes(PERMISSIONS.USER_READ);
    const canCreateUsers = currentPermissions.includes(PERMISSIONS.USER_CREATE);
    const canUpdateUsers = currentPermissions.includes(PERMISSIONS.USER_UPDATE);
    const canDeleteUsers = currentPermissions.includes(PERMISSIONS.USER_DELETE);
    const canUseUserActions = canUpdateUsers || canDeleteUsers;

    const [isInviteOpen, setIsInviteOpen] = useState(false);
    const [inviteFirstName, setInviteFirstName] = useState('');
    const [inviteLastName, setInviteLastName] = useState('');
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteRole, setInviteRole] = useState('');
    const [invitePhone, setInvitePhone] = useState('');

    useEffect(() => {
        let cancelled = false;

        const loadUsers = async () => {
            if (!canReadUsers) {
                setUsers([]);
                setUsersError('Nao tem permissão para consultar utilizadores.');
                setUsersLoading(false);
                return;
            }

            setUsersLoading(true);
            setUsersError('');

            try {
                const fetchedUsers = await fetchUsers();
                if (!cancelled) setUsers(normalizeUsers(fetchedUsers));
            } catch (error) {
                if (!cancelled) {
                    setUsers([]);
                    setUsersError(getApiErrorMessage(error, 'Não foi possível carregar os utilizadores.'));
                }
            } finally {
                if (!cancelled) setUsersLoading(false);
            }
        };

        loadUsers();

        return () => {
            cancelled = true;
        };
    }, [canReadUsers]);

    useEffect(() => {
        let cancelled = false;

        const loadFleetObjects = async () => {
            try {
                const vehicles = await fetchVehicles();
                if (!cancelled) setEnrichedObjects(buildEnrichedObjects(vehicles));
            } catch {
                if (!cancelled) setEnrichedObjects(buildEnrichedObjects());
            }
        };

        loadFleetObjects();

        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        let cancelled = false;

        const loadAccessConfig = async () => {
            setRolesLoading(true);
            setPermissionsLoading(true);
            setRolesError('');
            setPermissionsError('');

            try {
                const [rolesResponse, permissionsResponse] = await Promise.all([
                    api.get(roleEndpoints.list),
                    api.get(permissionEndpoints.list),
                ]);
                const nextRoles = normalizeRoles(rolesResponse.data);
                const nextPermissions = normalizePermissions(permissionsResponse.data);
                const nextDefaults = {
                    ...defaultPermissionsByRole(rolesResponse.data),
                    ...defaultPermissionsMap(permissionsResponse.data),
                };

                if (!cancelled) {
                    setUserRoles(nextRoles);
                    setPermissionOptions(nextPermissions);
                    setDefaultRolePermissions(nextDefaults);
                    const firstRole = Object.keys(nextRoles)[0] || '';
                    setInviteRole(current => current && nextRoles[current] ? current : firstRole);
                }
            } catch (error) {
                if (!cancelled) {
                    setUserRoles({});
                    setPermissionOptions([]);
                    setDefaultRolePermissions({});
                    const message = getApiErrorMessage(error, 'Nao foi possível carregar perfis e permissões.');
                    setRolesError(message);
                    setPermissionsError(message);
                }
            } finally {
                if (!cancelled) {
                    setRolesLoading(false);
                    setPermissionsLoading(false);
                }
            }
        };

        loadAccessConfig();

        return () => {
            cancelled = true;
        };
    }, []);

    const toggleRole = (role) => {
        setFormRoles(prev => prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role]);
    };

    const filteredUsers = users.filter(user => {
        const q = searchQuery.toLowerCase();
        const matchesSearch = user.name.toLowerCase().includes(q) ||
            (user.nickname || '').toLowerCase().includes(q) ||
            user.email.toLowerCase().includes(q) ||
            (user.resourceReference || '').toLowerCase().includes(q);
        const matchesRole = roleFilter === 'all' || user.roles.includes(roleFilter);
        const matchesStatus = statusFilter === 'all' || user.status === statusFilter;
        return matchesSearch && matchesRole && matchesStatus;
    });

    const resetForm = () => {
        setFormFirstName('');
        setFormLastName('');
        setFormNickname('');
        setFormEmail('');
        setFormPhone('');
        setFormRoles([]);
        setFormResourceReferences([]);
        setFormPermissions([]);
        setFormCustomPerms(false);
    };

    const denyUserAction = () => {
        toast.error('Nao tem permissão para executar esta ação.');
    };

    const handleOpenCreate = () => {
        if (!canCreateUsers) {
            denyUserAction();
            return;
        }
        resetForm();
        setIsCreateOpen(true);
    };

    const handleOpenInvite = () => {
        if (!canCreateUsers) {
            denyUserAction();
            return;
        }
        resetInviteForm();
        setIsInviteOpen(true);
    };

    // Guarda a primeira referência para resumo e a lista completa para atribuições.
    const buildVehicleFromResourceReferences = (resourceReferences) => {
        if (!resourceReferences || resourceReferences.length === 0) return {resourceReference: null, vehicle: null, resourceReferences: [], resourceIds: []};
        const first = enrichedObjects.find(o => o.identifier === resourceReferences[0]);
        const resourceIds = resourceReferences
            .map(reference => enrichedObjects.find(o => o.identifier === reference)?.id)
            .filter(id => id !== undefined && id !== null);
        return {resourceReference: first?.identifier || null, vehicle: first?.name || null, resourceReferences, resourceIds};
    };

    const fetchAssignedResourceIds = async (userId) => {
        const response = await api.get(userResourceAssignmentEndpoints.list, {
            params: {user_id: userId},
        });
        return resourceIdsFromAssignmentsPayload(response.data);
    };

    const syncUserResourceAssignments = async (userId, nextResourceIds, fallbackCurrentResourceIds = []) => {
        if (!userId) return;

        const nextIds = uniqueValues(nextResourceIds);
        let currentIds = uniqueValues(fallbackCurrentResourceIds);

        try {
            currentIds = await fetchAssignedResourceIds(userId);
        } catch {
            // If the list endpoint is unavailable, still attach selected resources.
        }

        const removedIds = currentIds.filter(id => !nextIds.includes(id));
        const addedIds = nextIds.filter(id => !currentIds.includes(id));

        await Promise.all([
            ...removedIds.map(resourceId =>
                api.post(userResourceAssignmentEndpoints.detach, userResourceAssignmentPayload(userId, resourceId))
            ),
            ...addedIds.map(resourceId =>
                api.post(userResourceAssignmentEndpoints.attach, userResourceAssignmentPayload(userId, resourceId))
            ),
        ]);
    };

    const handleCreate = async () => {
        if (!canCreateUsers) {
            denyUserAction();
            return;
        }

        const fn = formFirstName.trim();
        const ln = formLastName.trim();
        const email = formEmail.trim();
        const phone = formPhone.trim();
        const nickname = formNickname.trim();
        if (!fn || !ln || !email) {
            toast.error('Nome, apelido e email são obrigatórios.');
            return;
        }
        if (phone && !/^\d{9}$/.test(phone)) {
            toast.error('O telefone deve ter 9 dígitos.');
            return;
        }
        if (formRoles.length === 0) {
            toast.error('Selecione pelo menos um tipo de utilizador.');
            return;
        }
        const vinfo = buildVehicleFromResourceReferences(formResourceReferences);
        const fallbackUser = {
            id: Math.max(...users.map(u => u.id), 0) + 1,
            firstName: fn, lastName: ln,
            name: `${fn} ${ln}`.trim(), nickname,
            email, phone,
            roles: formRoles, vehicle: vinfo.vehicle, resourceReference: vinfo.resourceReference, resourceReferences: vinfo.resourceReferences,
            resourceIds: vinfo.resourceIds,
            status: 'active', createdAt: new Date().toISOString().split('T')[0],
            _customPermissions: formCustomPerms,
            permissions: formCustomPerms ? [...formPermissions] : undefined,
        };

        setCreateSubmitting(true);
        try {
            const response = await api.post(userEndpoints.create, userPayload({
                fn,
                ln,
                nickname,
                email,
                phone,
                roles: formRoles,
                vinfo,
                customPerms: formCustomPerms,
                permissions: formPermissions,
            }));
            const newUser = normalizeUserResponse(response.data, fallbackUser);
            await syncUserResourceAssignments(newUser.id, vinfo.resourceIds, []);
            setUsers(prev => [newUser, ...prev]);
            setIsCreateOpen(false);
            toast.success(`Utilizador ${newUser.name} criado com sucesso!`);
        } catch (error) {
            toast.error(getApiErrorMessage(error, 'Não foi possível criar o utilizador.'));
        } finally {
            setCreateSubmitting(false);
        }
    };

    const handleOpenEdit = (user) => {
        if (!canUpdateUsers) {
            denyUserAction();
            return;
        }

        setSelectedUser(user);
        // Derivar firstName/lastName a partir do name se utilizador legado
        let fn = user.firstName, ln = user.lastName;
        if ((!fn || !ln) && user.name) {
            const parts = user.name.trim().split(/\s+/);
            fn = fn || parts[0] || '';
            ln = ln || parts.slice(1).join(' ') || '';
        }
        setFormFirstName(fn || '');
        setFormLastName(ln || '');
        setFormNickname(user.nickname || '');
        setFormEmail(user.email);
        setFormPhone(toLocalPhone(user.phone));
        setFormRoles([...user.roles]);
        setFormResourceReferences(user.resourceReferences || (user.resourceReference ? [user.resourceReference] : []));
        setFormCustomPerms(!!user._customPermissions);
        setFormPermissions(user._customPermissions ? (user.permissions || []) : effectivePermissions(user, defaultRolePermissions));
        setIsEditOpen(true);
    };

    const handleSaveEdit = async () => {
        if (!selectedUser) return;
        if (!canUpdateUsers) {
            denyUserAction();
            return;
        }

        const fn = formFirstName.trim();
        const ln = formLastName.trim();
        const email = formEmail.trim();
        const phone = formPhone.trim();
        const nickname = formNickname.trim();
        if (!fn || !ln || !email) {
            toast.error('Nome, apelido e email são obrigatórios.');
            return;
        }
        if (formRoles.length === 0) {
            toast.error('Selecione pelo menos um tipo de utilizador.');
            return;
        }
        if (phone && !/^\d{9}$/.test(phone)) {
            toast.error('O telefone deve ter 9 dígitos.');
            return;
        }
        const vinfo = buildVehicleFromResourceReferences(formResourceReferences);
        const fallbackUser = {
            ...selectedUser,
            firstName: fn, lastName: ln,
            name: `${fn} ${ln}`.trim(), nickname,
            email, phone,
            roles: formRoles, vehicle: vinfo.vehicle, resourceReference: vinfo.resourceReference, resourceReferences: vinfo.resourceReferences,
            resourceIds: vinfo.resourceIds,
            _customPermissions: formCustomPerms,
            permissions: formCustomPerms ? [...formPermissions] : undefined,
        };

        setEditSubmitting(true);
        try {
            const response = await api.post(userEndpoints.update, {
                id: selectedUser.id,
                user_id: selectedUser.id,
                ...userPayload({
                    fn,
                    ln,
                    nickname,
                    email,
                    phone,
                    roles: formRoles,
                    vinfo,
                    customPerms: formCustomPerms,
                    permissions: formPermissions,
                }),
            });
            const updatedUser = normalizeUserResponse(response.data, fallbackUser);
            await syncUserResourceAssignments(updatedUser.id, vinfo.resourceIds, selectedUser.resourceIds);
            setUsers(prev => prev.map(u => u.id === selectedUser.id ? updatedUser : u));
            setIsEditOpen(false);
            setSelectedUser(null);
            toast.success(`Utilizador ${updatedUser.name} atualizado com sucesso!`);
        } catch (error) {
            toast.error(getApiErrorMessage(error, 'Não foi possível atualizar o utilizador.'));
        } finally {
            setEditSubmitting(false);
        }
    };

    const resetInviteForm = () => {
        setInviteFirstName('');
        setInviteLastName('');
        setInviteEmail('');
        setInviteRole(Object.keys(userRoles)[0] || '');
        setInvitePhone('');
    };

    const handleSendInvite = () => {
        if (!canCreateUsers) {
            denyUserAction();
            return;
        }

        const fn = inviteFirstName.trim();
        const ln = inviteLastName.trim();
        if (!fn || !ln || !inviteEmail.trim()) {
            toast.error('Nome, apelido e email são obrigatórios.');
            return;
        }
        if (users.some(u => u.email.toLowerCase() === inviteEmail.trim().toLowerCase())) {
            toast.error('Já existe um utilizador com este email.');
            return;
        }
        const phone = invitePhone.trim();
        if (phone && !/^\d{9}$/.test(phone)) {
            toast.error('O telefone deve ter 9 dígitos.');
            return;
        }
        const fullName = `${fn} ${ln}`.trim();
        const tempPwd = randomPassword(10);
        // 1. Tenta criar conta na auth store (mock). Abort se falhar.
        const role = inviteRole === 'gestor' ? 'admin' : 'condutor';
        const r = adminResetPassword({email: inviteEmail.trim(), newPassword: tempPwd, name: fullName, role});
        if (!r.ok) {
            toast.error(r.error);
            return;
        }
        // 2. Cria entry na gestão de utilizadores
        const newUser = {
            id: Math.max(...users.map(u => u.id), 0) + 1,
            firstName: fn,
            lastName: ln,
            name: fullName,
            nickname: '',
            email: inviteEmail.trim(),
            phone,
            roles: [inviteRole],
            vehicle: null, resourceReference: null, resourceReferences: [],
            status: 'pending',
            createdAt: new Date().toISOString().split('T')[0],
            _invited: true,
            _tempPassword: tempPwd, // mock — em produção seria apenas enviado por email
        };
        setUsers(prev => [newUser, ...prev]);
        setIsInviteOpen(false);
        resetInviteForm();
        toast.success(
            `Convite enviado para ${newUser.email}. Palavra-passe temporária: ${tempPwd}`,
            {duration: 8000}
        );
    };

    const handleResendInvite = async (u) => {
        if (userActionSubmitting) return;
        if (!canUpdateUsers) {
            denyUserAction();
            return;
        }
        if (u.status !== 'pending') return;
        if (!window.confirm(`Reenviar convite para ${u.email}?`)) return;

        const actionKey = `resend:${u.id}`;
        setUserActionSubmitting(actionKey);
        try {
            const response = await api.post(userEndpoints.resendInvite, {
                id: u.id,
                user_id: u.id,
                email: u.email,
            });
            const fallbackUser = {
                ...u,
                _invited: true,
                _invitedAt: new Date().toISOString().split('T')[0],
            };
            const updatedUser = normalizeUserResponse(response.data, fallbackUser);
            setUsers(prev => prev.map(x => x.id === u.id ? updatedUser : x));
            toast.success(`Convite reenviado para ${updatedUser.email}.`);
        } catch (error) {
            toast.error(getApiErrorMessage(error, 'Não foi possível reenviar o convite.'));
        } finally {
            setUserActionSubmitting('');
        }
    };

    const handleToggleStatus = (user) => {
        if (userActionSubmitting) return;
        if (!canUpdateUsers) {
            denyUserAction();
            return;
        }
        if (user.status === 'pending') return;
        setConfirmationAction({type: 'status', user});
    };

    const executeToggleStatus = async (user) => {
        const nextStatus = user.status === 'active' ? 'inactive' : 'active';
        const actionKey = `status:${user.id}`;
        setUserActionSubmitting(actionKey);
        try {
            const response = await api.post(userEndpoints.toggleStatus, {
                id: user.id,
                user_id: user.id,
                status: nextStatus,
            });
            const updatedUser = normalizeUserResponse(response.data, {...user, status: nextStatus});
            setUsers(prev => prev.map(u => u.id === user.id ? updatedUser : u));
            toast.success(`${updatedUser.name} ${nextStatus === 'inactive' ? 'desativado' : 'ativado'}.`);
        } catch (error) {
            toast.error(getApiErrorMessage(error, 'Não foi possível atualizar o estado do utilizador.'));
        } finally {
            setUserActionSubmitting('');
            setConfirmationAction(null);
        }
    };

    const handleDelete = (user) => {
        if (userActionSubmitting) return;
        if (!canDeleteUsers) {
            denyUserAction();
            return;
        }
        setConfirmationAction({type: 'delete', user});
    };

    const executeDelete = async (user) => {
        const actionKey = `delete:${user.id}`;
        setUserActionSubmitting(actionKey);
        try {
            await api.post(userEndpoints.delete, {
                id: user.id,
                user_id: user.id,
            });
            setUsers(prev => prev.filter(u => u.id !== user.id));
            toast.success(`Utilizador ${user.name} removido.`);
        } catch (error) {
            toast.error(getApiErrorMessage(error, 'Não foi possível remover o utilizador.'));
        } finally {
            setUserActionSubmitting('');
            setConfirmationAction(null);
        }
    };

    const handleConfirmUserAction = () => {
        if (!confirmationAction?.user || userActionSubmitting) return;
        if (confirmationAction.type === 'status') {
            executeToggleStatus(confirmationAction.user);
            return;
        }
        if (confirmationAction.type === 'delete') {
            executeDelete(confirmationAction.user);
        }
    };

    const closeConfirmationDialog = (open) => {
        if (!open && !userActionSubmitting) setConfirmationAction(null);
    };

    const confirmationUser = confirmationAction?.user;
    const confirmationIsStatus = confirmationAction?.type === 'status';
    const confirmationNextStatus = confirmationUser?.status === 'active' ? 'inactive' : 'active';
    const confirmationSubmittingKey = confirmationAction
        ? `${confirmationAction.type}:${confirmationUser?.id}`
        : '';
    const confirmationSubmitting = userActionSubmitting === confirmationSubmittingKey;

    // Stats
    const totalActive = users.filter(u => u.status === 'active').length;
    const totalCondutores = users.filter(u => u.roles.includes('condutor')).length;
    const totalGestores = users.filter(u => u.roles.includes('gestor')).length;
    const totalManutencao = users.filter(u => u.roles.includes('manutencao')).length;
    const totalVigilantes = users.filter(u => u.roles.includes('vigilante')).length;

    // Referências já em uso por outros utilizadores (excluindo o em edição)
    const takenResourceReferences = new Set(
        users
            .flatMap(u => {
                if (selectedUser && u.id === selectedUser.id) return [];
                return u.resourceReferences && u.resourceReferences.length ? u.resourceReferences : (u.resourceReference ? [u.resourceReference] : []);
            })
    );

    const VehiclePicker = () => {
        const filtered = enrichedObjects.filter(o => {
            const q = pickerSearch.toLowerCase();
            if (!q) return true;
            return o.identifier.toLowerCase().includes(q) ||
                o.name.toLowerCase().includes(q) ||
                (o.tagId || '').toLowerCase().includes(q) ||
                (o.category || '').toLowerCase().includes(q);
        });
        const toggleResourceReference = (reference) => {
            setFormResourceReferences(prev => prev.includes(reference) ? prev.filter(item => item !== reference) : [...prev, reference]);
        };
        return (
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 space-y-3">
                <Label className="text-sm font-semibold text-blue-900">
                    Veículos / Equipamentos atribuídos (opcional)
                </Label>
                <p className="text-xs text-blue-700">Selecione 1 ou mais objetos da frota. Pesquise por referência, modelo, categoria ou tag.</p>

                <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
                    <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-between bg-white" data-testid="user-vehicle-trigger">
                            {formResourceReferences.length === 0 ? (
                                <span className="text-slate-500">— Nenhum atribuído —</span>
                            ) : (
                                <span className="text-slate-700 font-medium">{formResourceReferences.length} objeto(s) selecionado(s)</span>
                            )}
                            <ChevronDown size={14}/>
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[560px] p-0" align="start">
                        <div className="p-3 border-b border-slate-200">
                            <Input placeholder="Pesquisar por referência, modelo, categoria, tag..." value={pickerSearch}
                                   onChange={e => setPickerSearch(e.target.value)} className="h-9" data-testid="user-vehicle-search"/>
                        </div>
                        <div className="max-h-[320px] overflow-y-auto">
                            {filtered.length === 0 && <p className="p-4 text-sm text-center text-slate-500">Nenhum objeto encontrado.</p>}
                            {filtered.map(o => {
                                const selected = formResourceReferences.includes(o.identifier);
                                const taken = !selected && takenResourceReferences.has(o.identifier);
                                return (
                                    <button key={o.uid} type="button" disabled={taken}
                                            onClick={() => toggleResourceReference(o.identifier)}
                                            data-testid={`vehicle-option-${o.identifier}`}
                                            className={`w-full text-left flex items-center gap-3 px-3 py-2.5 border-b border-slate-100 last:border-0 transition-colors ${
                                                selected ? 'bg-blue-50' : taken ? 'opacity-40 cursor-not-allowed' : 'hover:bg-slate-50'
                                            }`}>
                                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${selected ? 'bg-blue-600 border-blue-600' : 'border-slate-300 bg-white'}`}>
                                            {selected && <Check size={12} className="text-white"/>}
                                        </div>
                                        <Badge variant="outline" className="text-[10px] uppercase font-mono">
                                            {TYPE_LABELS[o.type] || o.type}
                                        </Badge>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <span className="font-mono font-bold text-sm text-slate-900">{o.identifier}</span>
                                                <span className="text-xs text-slate-500">·</span>
                                                <span className="text-sm text-slate-700 truncate">{o.name}</span>
                                            </div>
                                            <div className="flex items-center gap-2 mt-0.5 text-[11px] text-slate-500">
                                                <span>📂 {o.category}</span>
                                                <span className="text-slate-300">·</span>
                                                {o.tagId ? <span className="font-mono text-purple-600">🏷 {o.tagId}</span> : <span className="italic text-slate-400">sem tag</span>}
                                                {o.hasDriver ? (
                                                    <><span className="text-slate-300">·</span><span className="text-emerald-600">👤 {o.driverName}</span></>
                                                ) : null}
                                            </div>
                                        </div>
                                        {taken && <span className="text-[10px] text-amber-600 ml-2">(já atribuído)</span>}
                                    </button>
                                );
                            })}
                        </div>
                        <div className="p-2 border-t border-slate-200 flex items-center justify-between bg-slate-50">
                            <span className="text-xs text-slate-500">{formResourceReferences.length} selecionado(s)</span>
                            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setFormResourceReferences([])}>Limpar</Button>
                        </div>
                    </PopoverContent>
                </Popover>

                {/* Chips dos selecionados */}
                {formResourceReferences.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                        {formResourceReferences.map(p => {
                            const o = enrichedObjects.find(x => x.identifier === p);
                            if (!o) return null;
                            return (
                                <Badge key={p} className="bg-white border border-blue-300 text-blue-900 hover:bg-blue-100 cursor-pointer flex items-center gap-1.5 py-1 px-2"
                                       onClick={() => setFormResourceReferences(prev => prev.filter(x => x !== p))}>
                                    <span className="font-mono font-bold">{o.identifier}</span>
                                    <span className="text-[10px] text-slate-500">{o.name}</span>
                                    <span className="text-red-500 ml-1">×</span>
                                </Badge>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    };

    const RoleSelector = () => (
        <div className="space-y-2">
            <Label className="text-sm font-semibold">Tipo de Utilizador *</Label>
            <p className="text-xs text-slate-500 mb-2">
                Pode escolher <strong>uma ou mais</strong> opções — por exemplo, utilizador e gestor. Não é obrigatório selecionar todas.
            </p>
            {rolesLoading && <p className="text-xs text-slate-500">A carregar perfis...</p>}
            {rolesError && <p className="text-xs text-red-600">{rolesError}</p>}
            {!rolesLoading && !rolesError && Object.keys(userRoles).length === 0 && (
                <p className="text-xs text-slate-500">Nenhum perfil disponivel.</p>
            )}
            <div className="grid grid-cols-2 gap-2">
                {Object.entries(userRoles).map(([key, role]) => {
                    const Icon = role.icon;
                    const selected = formRoles.includes(key);
                    return (
                        <button key={key} type="button"
                                data-testid={`role-option-${key}`}
                                className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-all text-left ${selected ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-200' : 'border-slate-200 hover:border-slate-300'}`}
                                onClick={() => toggleRole(key)}>
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${selected ? 'bg-blue-600' : 'bg-slate-100'}`}>
                                <Icon size={20} className={selected ? 'text-white' : 'text-slate-500'}/>
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className={`font-medium text-sm ${selected ? 'text-blue-900' : 'text-slate-700'}`}>{role.label}</p>
                                <p className="text-xs text-slate-500 truncate">{role.desc}</p>
                            </div>
                            {selected && <CheckCircle2 size={18} className="text-blue-600 flex-shrink-0"/>}
                        </button>
                    );
                })}
            </div>
        </div>
    );

    return (
        <Layout>
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-slate-900 text-3xl font-secondary font-bold tracking-tight">Gestão de Utilizadores</h1>
                        <p className="text-slate-600 font-primary mt-1">Utilizadores, gestores, manutenção e vigilantes</p>
                    </div>
                    {canCreateUsers && (
                        <div className="flex items-center gap-2">
                            {/*
                            <Button
                                variant="outline"
                                className="border-violet-300 text-violet-700 hover:bg-violet-50"
                                onClick={handleOpenInvite}
                                data-testid="invite-user-btn"
                            >
                                <Send size={16} className="mr-2"/> Convidar por email
                            </Button>
                            */}
                            <Button data-testid="new-user-btn" className="bg-blue-600 hover:bg-blue-700" onClick={handleOpenCreate}>
                                <Plus size={18} className="mr-2"/>
                                Novo Utilizador
                            </Button>
                        </div>
                    )}
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <Card className="p-4">
                        <div className="flex items-center justify-between mb-1"><p className="text-slate-600 text-xs font-medium">Total Ativos</p><User className="text-blue-600" size={18}/></div>
                        <p className="text-2xl font-secondary font-bold text-slate-900">{totalActive}</p>
                        <p className="text-xs text-slate-500">{users.length} registados</p>
                    </Card>
                    <Card className="p-4">
                        <div className="flex items-center justify-between mb-1"><p className="text-slate-600 text-xs font-medium">Utilizadores</p><Car className="text-blue-600" size={18}/></div>
                        <p className="text-2xl font-secondary font-bold text-slate-900">{totalCondutores}</p>
                    </Card>
                    <Card className="p-4">
                        <div className="flex items-center justify-between mb-1"><p className="text-slate-600 text-xs font-medium">Gestores</p><Shield className="text-purple-600" size={18}/></div>
                        <p className="text-2xl font-secondary font-bold text-slate-900">{totalGestores}</p>
                    </Card>
                    <Card className="p-4">
                        <div className="flex items-center justify-between mb-1"><p className="text-slate-600 text-xs font-medium">Manutenção</p><Wrench className="text-amber-600" size={18}/></div>
                        <p className="text-2xl font-secondary font-bold text-slate-900">{totalManutencao}</p>
                    </Card>
                    <Card className="p-4">
                        <div className="flex items-center justify-between mb-1"><p className="text-slate-600 text-xs font-medium">Vigilantes</p><Eye className="text-emerald-600" size={18}/></div>
                        <p className="text-2xl font-secondary font-bold text-slate-900">{totalVigilantes}</p>
                    </Card>
                </div>

                {/* Filters */}
                <div className="flex flex-wrap gap-3">
                    <div className="flex-1 min-w-[250px] relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16}/>
                        <Input placeholder="Pesquisar por nome, alcunha, email ou referência..." className="pl-9" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}/>
                    </div>
                    <Select value={roleFilter} onValueChange={setRoleFilter}>
                        <SelectTrigger className="w-[180px]"><SelectValue/></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Todos os Tipos</SelectItem>
                            {Object.entries(userRoles).map(([key, role]) => (
                                <SelectItem key={key} value={key}>{role.label}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger className="w-[140px]"><SelectValue/></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Todos</SelectItem>
                            <SelectItem value="active">Ativos</SelectItem>
                            <SelectItem value="inactive">Inativos</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                {/* Users Table */}
                <Card>
                    <div className="p-4 border-b border-slate-200">
                        <h2 className="font-secondary font-bold text-slate-900">Utilizadores ({filteredUsers.length})</h2>
                    </div>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Nome</TableHead>
                                <TableHead>Contacto</TableHead>
                                <TableHead>Tipo(s)</TableHead>
                                <TableHead>Veículo Atribuído</TableHead>
                                <TableHead>Estado</TableHead>
                                {canUseUserActions && <TableHead className="w-[120px]">Ações</TableHead>}
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filteredUsers.map(user => (
                                <TableRow key={user.id} className="hover:bg-slate-50" data-testid={`user-row-${user.id}`}>
                                    <TableCell>
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
                                                <span className="text-white font-bold text-sm">{user.name.split(' ').map(n => n[0]).join('').substring(0, 2)}</span>
                                            </div>
                                            <div>
                                                <p className="font-medium text-slate-900">
                                                    {user.name}
                                                    {user.nickname && <span className="text-slate-500 font-normal ml-1">({user.nickname})</span>}
                                                </p>
                                                <p className="text-xs text-slate-500">Desde {user.createdAt}</p>
                                            </div>
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <div className="space-y-1">
                                            <p className="text-sm text-slate-700 flex items-center gap-1"><Mail size={12} className="text-slate-400"/>{user.email}</p>
                                            <p className="text-sm text-slate-700 flex items-center gap-1"><Phone size={12} className="text-slate-400"/>{user.phone}</p>
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex flex-wrap gap-1">
                                            {user.roles.map(role => {
                                                const conf = userRoles[role];
                                                if (!conf) return null;
                                                return <Badge key={role} className={`${conf.color} border-0 text-xs`}>{conf.label}</Badge>;
                                            })}
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        {(() => {
                                            const ps = user.resourceReferences && user.resourceReferences.length ? user.resourceReferences : (user.resourceReference ? [user.resourceReference] : []);
                                            if (ps.length === 0) return <span className="text-xs text-slate-400 italic">Sem objetos</span>;
                                            const first = enrichedObjects.find(o => o.identifier === ps[0]);
                                            return (
                                                <div className="flex items-center gap-2">
                                                    <Car size={14} className="text-blue-600"/>
                                                    <div>
                                                        <p className="text-sm font-medium text-slate-900">
                                                            {first?.name || user.vehicle || ps[0]}
                                                            {ps.length > 1 && <span className="text-xs text-blue-600 ml-1">+{ps.length - 1}</span>}
                                                        </p>
                                                        <p className="text-xs text-slate-500 font-mono">{ps.join(', ')}</p>
                                                    </div>
                                                </div>
                                            );
                                        })()}
                                    </TableCell>
                                    <TableCell>
                                        <Badge className={user.status === 'active' ? 'bg-emerald-100 text-emerald-700' : user.status === 'pending' ? 'bg-violet-100 text-violet-700' : 'bg-slate-100 text-slate-600'}>
                                            {user.status === 'active' ? 'Ativo' : user.status === 'pending' ? 'Convite pendente' : 'Inativo'}
                                        </Badge>
                                        {user._customPermissions && (
                                            <Badge variant="outline" className="ml-1 text-[9px] border-violet-300 text-violet-700">
                                                <ShieldCheck size={10} className="mr-0.5"/> custom
                                            </Badge>
                                        )}
                                    </TableCell>
                                    {canUseUserActions && (
                                        <TableCell>
                                            <div className="flex items-center gap-1">
                                                {canUpdateUsers && (
                                                    <>
                                                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => handleOpenEdit(user)} data-testid={`edit-user-${user.id}`}>
                                                            <Edit size={14} className="text-slate-600"/>
                                                        </Button>
                                                        {user.status === 'pending' && (
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                className="h-8 w-8 p-0"
                                                                onClick={() => handleResendInvite(user)}
                                                                disabled={userActionSubmitting === `resend:${user.id}`}
                                                                data-testid={`resend-invite-${user.id}`}
                                                                title="Reenviar convite"
                                                            >
                                                                <RotateCw size={14} className="text-amber-600"/>
                                                            </Button>
                                                        )}
                                                        {user.status !== 'pending' && (
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                className="h-8 w-8 p-0"
                                                                onClick={() => handleToggleStatus(user)}
                                                                disabled={userActionSubmitting === `status:${user.id}`}
                                                                data-testid={`toggle-user-${user.id}`}
                                                            >
                                                                {user.status === 'active' ? <XCircle size={14} className="text-amber-600"/> : <CheckCircle2 size={14} className="text-emerald-600"/>}
                                                            </Button>
                                                        )}
                                                    </>
                                                )}
                                                {canDeleteUsers && (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="h-8 w-8 p-0"
                                                        onClick={() => handleDelete(user)}
                                                        disabled={userActionSubmitting === `delete:${user.id}`}
                                                        data-testid={`delete-user-${user.id}`}
                                                    >
                                                        <Trash2 size={14} className="text-red-500"/>
                                                    </Button>
                                                )}
                                            </div>
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    {(usersLoading || usersError || filteredUsers.length === 0) && (
                        <div className="p-12 text-center text-slate-500">
                            <User className="mx-auto mb-3 text-slate-300" size={48}/>
                            <p className="font-medium">
                                {usersLoading ? 'A carregar utilizadores...' : usersError || 'Nenhum utilizador encontrado'}
                            </p>
                        </div>
                    )}
                </Card>

                {/* User Action Confirmation Dialog */}
                <Dialog open={!!confirmationAction} onOpenChange={closeConfirmationDialog}>
                    <DialogContent className="max-w-md" data-testid="user-action-confirmation-dialog">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                {confirmationIsStatus ? (
                                    confirmationNextStatus === 'inactive' ? (
                                        <XCircle className="text-amber-600" size={20}/>
                                    ) : (
                                        <CheckCircle2 className="text-emerald-600" size={20}/>
                                    )
                                ) : (
                                    <Trash2 className="text-red-600" size={20}/>
                                )}
                                {confirmationIsStatus
                                    ? `${confirmationNextStatus === 'inactive' ? 'Desativar' : 'Ativar'} utilizador`
                                    : 'Remover utilizador'}
                            </DialogTitle>
                            <DialogDescription>
                                {confirmationIsStatus
                                    ? `Tem a certeza que pretende ${confirmationNextStatus === 'inactive' ? 'desativar' : 'ativar'} ${confirmationUser?.name}?`
                                    : `Tem a certeza que pretende remover ${confirmationUser?.name}? Esta acao nao pode ser anulada.`}
                            </DialogDescription>
                        </DialogHeader>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setConfirmationAction(null)} disabled={confirmationSubmitting}>
                                Cancelar
                            </Button>
                            <Button
                                variant={confirmationIsStatus ? 'default' : 'destructive'}
                                className={confirmationIsStatus ? (confirmationNextStatus === 'inactive' ? 'bg-amber-600 hover:bg-amber-700' : 'bg-emerald-600 hover:bg-emerald-700') : ''}
                                onClick={handleConfirmUserAction}
                                disabled={confirmationSubmitting}
                                data-testid="confirm-user-action"
                            >
                                {confirmationSubmitting
                                    ? 'A processar...'
                                    : confirmationIsStatus
                                        ? (confirmationNextStatus === 'inactive' ? 'Desativar' : 'Ativar')
                                        : 'Remover'}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Create User Dialog */}
                <Dialog open={canCreateUsers && isCreateOpen} onOpenChange={(open) => setIsCreateOpen(canCreateUsers && open)}>
                    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2"><Plus className="text-blue-600" size={20}/>Novo Utilizador</DialogTitle>
                            <DialogDescription>Criar um novo utilizador no sistema</DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2"><Label>Nome *</Label><Input value={formFirstName} onChange={(e) => setFormFirstName(e.target.value)} placeholder="Ex: João" data-testid="user-first-name-input"/></div>
                                <div className="space-y-2"><Label>Apelido *</Label><Input value={formLastName} onChange={(e) => setFormLastName(e.target.value)} placeholder="Ex: Silva" data-testid="user-last-name-input"/></div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Alcunha</Label>
                                    <Input value={formNickname} onChange={(e) => setFormNickname(e.target.value)} placeholder="Ex: Jota" data-testid="user-nickname-input"/>
                                    <p className="text-xs text-slate-500">Aparecerá entre parêntesis. Útil quando há nomes parecidos.</p>
                                </div>
                                <div className="space-y-2"><Label>Telefone</Label><Input value={formPhone} onChange={(e) => setFormPhone(toLocalPhone(e.target.value))} placeholder="912345678" inputMode="numeric" maxLength={9}/></div>
                            </div>
                            <div className="space-y-2"><Label>Email *</Label><Input type="email" value={formEmail} onChange={(e) => setFormEmail(e.target.value)} placeholder="joao@empresa.pt"/></div>
                            <RoleSelector/>
                            {formRoles.includes('condutor') && <VehiclePicker/>}
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setIsCreateOpen(false)} disabled={createSubmitting}>Cancelar</Button>
                            <Button className="bg-blue-600 hover:bg-blue-700" onClick={handleCreate} disabled={createSubmitting} data-testid="create-user-submit">
                                <Plus size={16} className="mr-2"/>{createSubmitting ? 'A criar...' : 'Criar Utilizador'}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Edit User Dialog */}
                <Dialog open={canUpdateUsers && isEditOpen} onOpenChange={(open) => setIsEditOpen(canUpdateUsers && open)}>
                    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2"><Edit className="text-blue-600" size={20}/>Editar Utilizador</DialogTitle>
                            <DialogDescription>Atualizar informações de {selectedUser?.name}</DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2"><Label>Nome *</Label><Input value={formFirstName} onChange={(e) => setFormFirstName(e.target.value)} data-testid="user-edit-first-name-input"/></div>
                                <div className="space-y-2"><Label>Apelido *</Label><Input value={formLastName} onChange={(e) => setFormLastName(e.target.value)} data-testid="user-edit-last-name-input"/></div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Alcunha</Label>
                                    <Input value={formNickname} onChange={(e) => setFormNickname(e.target.value)} placeholder="Ex: Jota"/>
                                </div>
                                <div className="space-y-2"><Label>Telefone</Label><Input value={formPhone} onChange={(e) => setFormPhone(toLocalPhone(e.target.value))} placeholder="912345678" inputMode="numeric" maxLength={9}/></div>
                            </div>
                            <div className="space-y-2"><Label>Email *</Label><Input type="email" value={formEmail} onChange={(e) => setFormEmail(e.target.value)}/></div>
                            <RoleSelector/>
                            {formRoles.includes('condutor') && <VehiclePicker/>}

                            {/* Permissões granulares (pedido c) */}
                            <div className="border border-slate-200 rounded-lg p-3 space-y-3 bg-slate-50">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <ShieldCheck size={16} className="text-violet-600"/>
                                        <Label className="text-sm font-semibold">Permissões granulares</Label>
                                    </div>
                                    {/*
                                    <label className="flex items-center gap-2 text-xs cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={formCustomPerms}
                                            onChange={e => {
                                                setFormCustomPerms(e.target.checked);
                                                if (e.target.checked && formPermissions.length === 0) {
                                                    // Pré-preencher com defaults do primeiro role
                                                    const first = formRoles[0];
                                                    setFormPermissions(first ? [...(defaultRolePermissions[first] || [])] : []);
                                                }
                                            }}
                                            data-testid="toggle-custom-perms"
                                        />
                                        <span className="text-slate-700">Personalizar</span>
                                    </label>
                                    */}
                                </div>
                                {formCustomPerms ? (
                                    <>
                                        {permissionsLoading && <p className="text-xs text-slate-500">A carregar permissões...</p>}
                                        {permissionsError && <p className="text-xs text-red-600">{permissionsError}</p>}
                                        {!permissionsLoading && !permissionsError && permissionOptions.length === 0 && (
                                            <p className="text-xs text-slate-500">Nenhuma permissão disponivel.</p>
                                        )}
                                        <div className="grid grid-cols-2 gap-2">
                                            {permissionOptions.map(p => (
                                                <label key={p.id} className="flex items-start gap-2 p-2 rounded border border-slate-200 bg-white hover:bg-slate-50 cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        checked={formPermissions.includes(p.id)}
                                                        onChange={e => setFormPermissions(prev => e.target.checked ? [...prev, p.id] : prev.filter(x => x !== p.id))}
                                                        className="mt-0.5"
                                                        data-testid={`perm-${p.id}`}
                                                    />
                                                    <div>
                                                        <p className="text-xs font-medium text-slate-800">{p.label}</p>
                                                        <p className="text-[10px] text-slate-500">{p.desc}</p>
                                                    </div>
                                                </label>
                                            ))}
                                        </div>
                                    </>
                                ) : (
                                    <p className="text-xs text-slate-500 italic">
                                        A usar permissões default dos perfis ({formRoles.length ? formRoles.join(' + ') : '—'}).
                                        Marque <em>Personalizar</em> para override granular.
                                    </p>
                                )}
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setIsEditOpen(false)} disabled={editSubmitting}>Cancelar</Button>
                            <Button className="bg-blue-600 hover:bg-blue-700" onClick={handleSaveEdit} disabled={editSubmitting} data-testid="save-edit-user">
                                {editSubmitting ? 'A guardar...' : 'Guardar Alterações'}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
                {/* Invite dialog (pedido d) */}
                <Dialog open={canCreateUsers && isInviteOpen} onOpenChange={(open) => setIsInviteOpen(canCreateUsers && open)}>
                    <DialogContent className="max-w-md" data-testid="invite-user-dialog">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <Send size={18} className="text-violet-600"/>
                                Convidar novo utilizador
                            </DialogTitle>
                            <DialogDescription>
                                É gerada uma palavra-passe temporária e a conta é criada com estado <em>convite pendente</em>.
                                Em produção, o convite é enviado por email; neste ambiente mock, a palavra-passe é mostrada na notificação.
                            </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-3 py-2">
                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-1.5">
                                    <Label>Nome *</Label>
                                    <Input value={inviteFirstName} onChange={e => setInviteFirstName(e.target.value)} placeholder="Ex: João" data-testid="invite-first-name"/>
                                </div>
                                <div className="space-y-1.5">
                                    <Label>Apelido *</Label>
                                    <Input value={inviteLastName} onChange={e => setInviteLastName(e.target.value)} placeholder="Ex: Silva" data-testid="invite-last-name"/>
                                </div>
                            </div>
                            <div className="space-y-1.5">
                                <Label>Email *</Label>
                                <Input type="email" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} placeholder="nome@empresa.pt" data-testid="invite-email"/>
                            </div>
                            <div className="space-y-1.5">
                                <Label>Telefone</Label>
                                <Input value={invitePhone} onChange={e => setInvitePhone(toLocalPhone(e.target.value))} placeholder="912345678" inputMode="numeric" maxLength={9}/>
                            </div>
                            <div className="space-y-1.5">
                                <Label>Perfil</Label>
                                <Select value={inviteRole} onValueChange={setInviteRole}>
                                    <SelectTrigger data-testid="invite-role"><SelectValue/></SelectTrigger>
                                    <SelectContent>
                                        {Object.entries(userRoles).map(([key, v]) => (
                                            <SelectItem key={key} value={key}>{v.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setIsInviteOpen(false)}>Cancelar</Button>
                            <Button className="bg-violet-600 hover:bg-violet-700" onClick={handleSendInvite} data-testid="invite-submit">
                                <Send size={14} className="mr-1"/> Enviar convite
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </Layout>
    );
};
